import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useEffect, useState } from "react";
import axiosConfig from "../../../services/Axios";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-solid-svg-icons";
import { faCircleUser, faKey } from "@fortawesome/pro-regular-svg-icons";
import PasswordRequirement from "../../../components/ui/PasswordRequirement";
import FieldError from "../../../components/ui/FieldError";
import Button from "../../../components/ui/Button";
import { Link } from "react-router-dom";
import Title from "../../../components/ui/Title";
import { toast } from "react-toastify";

function UsersForm({ userId }) {
  const params = useParams();
  const [user, setUser] = useState();
  const authUser = useSelector((state) => state.auth);
  const form = useForm();
  const [passwordShown, setPasswordShown] = useState(false);
  const { t } = useTranslation();

  const loadUser = () => {
    axiosConfig
      .get(
        process.env.REACT_APP_API_URL +
          `/api/users/${userId ? userId : params.id}`
      )
      .then((response) => {
        if (response?.status === 200 && response?.data) {
          setUser(response.data);
        }
      });
  };

  const togglePasswordShown = () => {
    setPasswordShown(!passwordShown);
  };

  useEffect(() => {
    loadUser();
  }, []);

  const onSubmit = async (data) => {
    try {
      await axiosConfig
        .patch(
          process.env.REACT_APP_API_URL + "/api/users/" + user.id,
          { plainPassword: data.plainPassword },
          {
            headers: { "Content-Type": "application/merge-patch+json" },
          }
        )
        .then((response) => {
          if (response?.status === 200 && response?.data) {
            toast.success(t("global.changes_saved"), {
              theme: "colored",
              hideProgressBar: true,
            });

            form.reset();
          }
        });
    } catch ({ response }) {
      if (response?.data?.violations) {
        form.setError("plainPassword", {
          type: "manual",
          message: response.data.violations[0].message,
        });
      }
    }
  };

  return (
    <div className="lg:ml-6 text-sm mt-10 lg:mt-0 col-span-12 lg:col-span-9 xl:col-span-10">
      <div className="lg:p-8 lg:px-10 lg:bg-white rounded-lg">
        {user && (
          <>
            <Title
              icon={faCircleUser}
              title={
                authUser?.user?.id === user.id
                  ? "Mon compte"
                  : "Compte " + user.email
              }
            />

            <div className="">
              <div className="text-lg font-bold text-bioport-orange">
                {user?.partner?.name.toUpperCase()}
              </div>
              <div className="grid grid-cols-2 mt-3 w-full gap-3">
                <div className="col-span-2 xl:col-span-1 p-4 px-5 shadow rounded-lg border border-gray-300 bg-white">
                  <div className="font-bold mb-4 pb-2 border-b border-gray-300">
                    {t("partnerContact.main").toUpperCase()} :
                  </div>
                  {user?.partner?.mainContact?.firstName}{" "}
                  {user?.partner?.mainContact?.lastName}
                  <br />
                  {user?.partner?.mainContact?.phoneNumber}
                  <br />
                </div>
                <div className="col-span-2 xl:col-span-1 p-4 px-5 shadow rounded-lg border border-gray-300  bg-white">
                  <div className="font-bold mb-4 pb-2 border-b border-gray-300">
                    {t("partnerLogisticAddress.main_address").toUpperCase()} :
                  </div>
                  {user?.partner?.mainAddress.address}
                  <br />
                  {user?.partner?.mainAddress?.zipcode} -{" "}
                  {user?.partner?.mainAddress?.city}
                  <br />
                  {user?.partner?.mainAddress?.country?.labelFr}
                </div>
              </div>
            </div>

            <div className="flex mt-6">
              <Link to={`/settings/partners/${user?.partner.id}`}>
                <div className="btn btn-primary">{t('global.btn.update')}</div>
              </Link>
            </div>
          </>
        )}
      </div>

      <div className="lg:p-8 lg:px-10 lg:bg-white rounded-lg  mt-8">
        <Title icon={faKey} title={t("login.pass")} />

        <form onSubmit={form.handleSubmit(onSubmit)} className="">
          <div className="lg:flex gap-3">
            <div className="lg:w-1/2">
              <div className="relative">
                <input
                  {...form.register("plainPassword")}
                  name="plainPassword"
                  type={passwordShown ? "text" : "password"}
                  className="w-full"
                  aria-invalid={
                    form.formState?.errors.plainPassword ? "true" : "false"
                  }
                  placeholder={t("login.password.new_pass")}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                  <FontAwesomeIcon
                    icon={passwordShown ? faEye : faEyeSlash}
                    className={`${
                      passwordShown ? "text-bioport-blue-dark" : "text-gray-400"
                    } cursor-pointer mr-2`}
                    onClick={() => togglePasswordShown()}
                  />
                </div>
              </div>
              <FieldError error={form.formState?.errors?.plainPassword} />
            </div>

            <div className="mt-2 lg:mt-0 lg:w-1/2">
              <input
                {...form.register("password_repeat", {
                  validate: (value) =>
                    value === form.watch("plainPassword") ||
                    t("login.password.password_match"),
                })}
                aria-invalid={
                  form.formState?.errors.plainPassword ? "true" : "false"
                }
                name="password_repeat"
                type={passwordShown ? "text" : "password"}
                className="w-full"
                placeholder={t("login.password.repeat_pass")}
              />
              <FieldError errors={form.formState?.errors?.password_repeat} />
            </div>
          </div>

          <PasswordRequirement />

          <Button
            className="btn btn-primary mt-6"
            isloading={form.formState.isSubmitting}
          >
            {t("global.update")}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default UsersForm;
